import { CameraControls, CameraControlsImpl } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useCallback } from 'react';
import { useShallow } from 'zustand/shallow';

import { useCameraControlsEvents } from 'app/modules/wallforming/components/scenes/camera-controls/hooks/useCameraControlsEvents';
import { useMainCameraSyncHandler } from 'app/modules/wallforming/components/scenes/camera-controls/hooks/useMainCameraSyncHandler';
import { useOBCSetup } from 'app/modules/wallforming/components/scenes/camera-controls/hooks/useOBCSetup';
import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const MainCameraControls = () => {
    const {
        mainCameraControlsRef,
        setMainWebGLRendererRef,
        setMainRaycasterRef,
        setMainCameraControlsRef,
        mainCameraSettings
    } = usePlacementStore(
        useShallow((state) => ({
            mainCameraControlsRef: state.mainCameraControlsRef,
            setMainWebGLRendererRef: state.setMainWebGLRendererRef,
            setMainRaycasterRef: state.setMainRaycasterRef,
            setMainCameraControlsRef: state.setMainCameraControlsRef,
            mainCameraSettings: state.mainCameraSettings
        }))
    );

    const raycaster = useThree((state) => state.raycaster);
    const gl = useThree((state) => state.gl);

    useCameraControlsEvents({ cameraControlsRef: mainCameraControlsRef, isMainCamera: true });

    useOBCSetup();
    useMainCameraSyncHandler();

    // Initialize camera controls, raycaster, and WebGL renderer references
    const handleMainCameraControlsRef = useCallback(
        (cameraControls: CameraControls | null) => {
            if (!cameraControls) {
                setMainCameraControlsRef(null);
                return;
            }

            cameraControls.camera.layers.enableAll();
            cameraControls.camera.up.set(0, 0, 1);
            cameraControls.camera.updateProjectionMatrix();

            setMainCameraControlsRef(cameraControls);

            if (raycaster) {
                raycaster.layers.disableAll();
                raycaster.layers.enable(LayerType.RAYCAST);
                setMainRaycasterRef(raycaster);
            }

            if (gl) {
                setMainWebGLRendererRef(gl);
            }
        },
        [gl, raycaster, setMainCameraControlsRef, setMainRaycasterRef, setMainWebGLRendererRef]
    );

    return (
        <CameraControls
            ref={handleMainCameraControlsRef}
            azimuthRotateSpeed={mainCameraSettings.azimuthRotateSpeed}
            dollySpeed={mainCameraSettings.dollySpeed}
            dollyToCursor={mainCameraSettings.dollyToCursor}
            draggingSmoothTime={mainCameraSettings.draggingSmoothTime}
            infinityDolly={true}
            mouseButtons={{
                left: CameraControlsImpl.ACTION.NONE,
                middle: CameraControlsImpl.ACTION.TRUCK,
                right: CameraControlsImpl.ACTION.NONE,
                wheel: CameraControlsImpl.ACTION.ZOOM
            }}
            polarRotateSpeed={mainCameraSettings.polarRotateSpeed}
            smoothTime={mainCameraSettings.smoothTime}
            truckSpeed={mainCameraSettings.truckSpeed}
            makeDefault
        />
    );
};
